import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import styled from "styled-components";

const PaginationContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 24px 0;
  font-family: "Urbanist";
`;

const PageNumber = styled.button`
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #12188a;
  background-color: ${(props) => (props.active ? "transparent" : "transparent")};
  color: ${(props) => (props.active ? "#12188a" : "#12188a")};
  cursor: pointer;
  font-family: "Urbanist";
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.active ? "#12188a" : "#f0f0f0")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavigationButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ArrowIcon = ({ direction = "right" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    style={{ transform: direction === "left" ? "rotate(180deg)" : "none" }}
  >
    <rect width="32" height="32" rx="8" fill="#12188A" />
    <path
      d="M12 20.59L16.58 16L12 11.41L13.41 10L19.41 16L13.41 22L12 20.59Z"
      fill="white"
    />
  </svg>
);

const Pagination = ({
  data = [],
  setPageData,
  itemsPerPage = 10,
  maxDisplayedPages = 5,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  useEffect(() => {
    if (data.length > 0) {
      updatePageData(currentPage);
    }
  }, [data, currentPage]);

  const updatePageData = (page) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPageData(data.slice(startIndex, endIndex));
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    updatePageData(page);
  };

  const getPageNumbers = () => {
    let pages = [];
    if (totalPages <= maxDisplayedPages) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      const halfDisplay = Math.floor(maxDisplayedPages / 2);
      let start = currentPage - halfDisplay;
      let end = currentPage + halfDisplay;

      if (start < 1) {
        start = 1;
        end = maxDisplayedPages;
      }
      if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxDisplayedPages + 1;
      }

      pages = Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
      );
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <PaginationContainer>
      <NavigationButton
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ArrowIcon direction="left" />
      </NavigationButton>

      {getPageNumbers().map((page) => (
        <PageNumber
          key={page}
          active={currentPage === page}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </PageNumber>
      ))}

      <NavigationButton
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ArrowIcon />
      </NavigationButton>
    </PaginationContainer>
  );
};

export default Pagination;